import { useEffect, useState } from 'preact/hooks';
import { api } from '../../api/client';
import type { ChartAnnotationDto, ListChartAnnotationsResponse } from '../../api/types';
import { MentionMarkdown } from './MentionMarkdown';
import './AnnotationList.css';

interface Props {
  plantId: string;
  /** Optional deep-link callback — the list can be embedded on the chart
   *  page (jump-to-annotation) or on the plant overview (open the chart at
   *  that annotation). Both flows re-use this component. */
  onJump?: (annotation: ChartAnnotationDto) => void;
  /** Author id for the "delete" affordance. Rows authored by others are
   *  read-only; the server would refuse a delete anyway. */
  currentUserId?: string | null;
  /** Optional refresh trigger — bump to reload from the server. */
  refreshKey?: number;
}

/**
 * Compact scrollable list of every annotation on a plant. Ordered by AtUtc
 * (oldest first — matches how the chart lays them out on the axis).
 */
export function AnnotationList({ plantId, onJump, currentUserId, refreshKey }: Props) {
  const [rows, setRows] = useState<ChartAnnotationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await api.get<ListChartAnnotationsResponse>(
          `/api/plants/${plantId}/annotations`,
          controller.signal,
        );
        if (!controller.signal.aborted) setRows(res.annotations);
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [plantId, refreshKey]);

  const deleteRow = async (r: ChartAnnotationDto) => {
    if (!currentUserId || r.authorUserId !== currentUserId) return;
    try {
      await api.delete(`/api/plants/${plantId}/annotations/${r.id}`);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Delete annotation failed', err);
    }
  };

  if (loading) return <div class="hv-ann-list__empty">Lade Anmerkungen…</div>;
  if (error) return <div class="hv-ann-list__empty">Fehler: {error}</div>;
  if (rows.length === 0) return <div class="hv-ann-list__empty">Noch keine Anmerkungen.</div>;

  return (
    <ul class="hv-ann-list">
      {rows.map((r) => (
        <li key={r.id} class="hv-ann-list__row">
          <div class="hv-ann-list__when">
            <div class="hv-ann-list__at">{formatAt(r)}</div>
            <div class="hv-ann-list__author">{r.authorDisplayName}</div>
          </div>
          <div class="hv-ann-list__body">
            <MentionMarkdown body={r.body} />
          </div>
          <div class="hv-ann-list__actions">
            {onJump && (
              <button
                type="button"
                class="hv-btn hv-btn--sm hv-btn--ghost"
                onClick={() => onJump(r)}
                title="Zur Annotation im Chart springen"
              >
                Öffnen
              </button>
            )}
            {currentUserId === r.authorUserId && (
              <button
                type="button"
                class="hv-btn hv-btn--sm hv-btn--ghost"
                onClick={() => void deleteRow(r)}
                title="Löschen (nur eigene)"
              >
                Löschen
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function formatAt(r: ChartAnnotationDto): string {
  const start = new Date(r.atUtc);
  if (r.kind === 'Range' && r.untilUtc) {
    const end = new Date(r.untilUtc);
    return `${start.toLocaleString()} – ${end.toLocaleString()}`;
  }
  return start.toLocaleString();
}
